import React, { useState, Dispatch, SetStateAction, PropsWithChildren } from 'react';
import { gql, Locale } from '@yi/core';
import { makeCouponQs } from '@yi/core/src/coupons';
import { i18n_buy_now } from '@yi/i18n/phrases/i18n_buy_now';
import { i18n_sign_up } from '@yi/i18n/phrases/i18n_sign_up';
import { i18n_upgrade_to_membership } from '@yi/i18n/phrases/i18n_upgrade_to_membership';

import { Button, ButtonLocalizedLink, useLocale } from '~/components';
import { useCurrentUser } from '~/src/common/user';
import { getLocalizedDefaultSignupPath } from '~/src/common/utilities';

import { DiscountText } from './DiscountText';
import { ButtonRow, MenuItem, Select, SplitItButton, TextPrice } from './elements';
import { PriceInfo } from './PriceInfo';

const { Free, Digital } = gql.typesClient.MembershipTier;

interface Props {
  buttons: gql.typesSsr.Query_Course_yi_Course_sales_page_select['buttons'];
  listPrice: number;
  setListPrice: Dispatch<SetStateAction<number>>;
  amountOff: number | null;
  percentOff: number | null;
  permissions: gql.typesSsr.Query_Course_yi_Course['permissions'] | null;
  urlTitle: string;
  discount: PriceInfo['discount'];
  courseType: 'bundle' | 'ecourse';
}

const getPurchaseUrl = (
  url: string,
  urlTitle: string,
  locale: string,
  courseType: 'bundle' | 'ecourse',
  queryString: string,
) => {
  if (url.includes('http')) {
    return `${url}${queryString}`;
  } else return `${locale === 'es' ? '/es' : ''}/${courseType}/purchase/${urlTitle}${queryString}`;
};

const getMembershipButtonInfo = (
  locale: Locale,
  isLoggedIn: boolean,
  memberShipTier: gql.typesClient.MembershipTier | undefined,
  permissions: gql.typesSsr.Query_Course_yi_Course['permissions'] | null,
) => {
  const isFree = permissions?.includes(Free);
  const isBasic = permissions?.includes(Digital);
  return {
    buttonText: !isLoggedIn
      ? isFree
        ? i18n_sign_up(locale)
        : i18n_sign_up(locale)
      : isBasic && memberShipTier === 'Free'
      ? i18n_upgrade_to_membership(locale)
      : i18n_upgrade_to_membership(locale),
    buttonUrl: getLocalizedDefaultSignupPath(locale),
  };
};

export const CourseButtons: React.FC<PropsWithChildren<Props>> = ({
  amountOff,
  buttons,
  discount,
  listPrice,
  percentOff,
  setListPrice,
  permissions,
  urlTitle,
  courseType,
}) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(0);
  const { locale } = useLocale();
  const { isLoggedIn, currentUser } = useCurrentUser();

  const showUpgradeBtn = listPrice === 0 && permissions?.includes(Digital);

  const onChange: React.ComponentProps<typeof Select>['onChange'] = event => {
    if (typeof event?.target.value !== 'number') {
      console.warn('Unexpected Value: ', event.target.value);
    } else {
      const index = event.target?.value;
      setValue(index);
      if (buttons?.[index]?.price) setListPrice(buttons[index]?.price || 0);
    }
  };

  const selectedButton = buttons?.[value] || null;
  const discountedPrice = amountOff
    ? listPrice - amountOff
    : percentOff
    ? listPrice * (1 - percentOff / 100)
    : listPrice;

  const couponQs = discount && 'couponCode' in discount ? makeCouponQs(discount.couponCode) : '';
  const { buttonText, buttonUrl } = getMembershipButtonInfo(
    locale,
    isLoggedIn,
    currentUser?.membership_tier,
    permissions,
  );

  const splitItMinimum = Number(process.env.GATSBY_SPLITIT_MINIMUM) || 999999;
  const isFree = permissions?.includes(Free);

  return (
    <>
      <ButtonRow>
        {discountedPrice > splitItMinimum &&
          !selectedButton?.disabled &&
          !selectedButton?.url.includes('http') && (
            <SplitItButton
              price={`$${Math.ceil(discountedPrice / 12)}`}
              path={getPurchaseUrl(
                selectedButton?.url || '',
                urlTitle,
                locale,
                courseType,
                couponQs,
              )}
            />
          )}

        {showUpgradeBtn && (
          <ButtonLocalizedLink
            to={
              isFree
                ? getPurchaseUrl(buttons?.[0]?.url || '', urlTitle, locale, courseType, '')
                : buttonUrl
            }
            style={{
              padding: '13.5px 70px',
              textTransform: 'none',
              color: 'white',
              marginBottom: 70,
              textDecoration: 'none',
              fontWeight: '700',
            }}
          >
            {buttonText.toUpperCase()}
          </ButtonLocalizedLink>
        )}
      </ButtonRow>
      {listPrice > 0 && (
        <TextPrice my={{ xs: 4, lg: 3 }}>
          <DiscountText
            testId="sales-page-banner-discount"
            price={listPrice || 0}
            discount={discount}
          />
        </TextPrice>
      )}
      {buttons && buttons.length > 1 && selectedButton ? (
        <>
          <Select
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            value={value}
            onChange={onChange}
            variant="filled"
            style={{ marginBottom: '20px' }}
          >
            {buttons.map((button, index) => (
              <MenuItem value={index}>{button?.text}</MenuItem>
            ))}
          </Select>
          {listPrice > 0 && (
            <Button
              data-testid="sales-page-purchase-button"
              as="a"
              kind="outlinedTurquoise"
              href={`${selectedButton.url}${couponQs}`}
              style={{ height: '55px', width: '100%', maxWidth: '180px' }}
              disabled={!!selectedButton?.disabled}
              onClick={e => selectedButton?.disabled && e.preventDefault()}
            >
              {i18n_buy_now(locale)}
            </Button>
          )}
        </>
      ) : (
        listPrice > 0 && (
          <Button
            as="a"
            kind="outlinedTurquoise"
            disabled={!!buttons?.[0]?.disabled}
            onClick={e => !!selectedButton?.disabled && e.preventDefault()}
            href={getPurchaseUrl(buttons?.[0]?.url || '', urlTitle, locale, courseType, couponQs)}
            style={{
              width: '100%',
              maxWidth: '180px',
              borderRadius: 4,
              fontWeight: 600,
              fontSize: 15,
              padding: '8px 32px',
              color: 'white',
            }}
            data-testid="sales-page-purchase-button"
          >
            {i18n_buy_now(locale)}
          </Button>
        )
      )}
    </>
  );
};
